import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { translations } from './translations';

// Create the language context
const LanguageContext = createContext({
  lang: 'es',
  t: translations.es,
  isEnglish: false,
  getPath: (path) => path,
});

// Hook to use language context
export const useLanguage = () => useContext(LanguageContext);

// Equivalent route slugs between Spanish (default) and English versions
const ES_TO_EN = {
  '/': '/en',
  '/filosofia': '/en/philosophy',
  '/proyectos': '/en/projects',
  '/publicidad': '/en/advertising',
  '/que-hacemos': '/en/what-we-do',
  '/hablemos': '/en/contact',
  '/legal': '/en/legal',
};
const EN_TO_ES = Object.fromEntries(
  Object.entries(ES_TO_EN).map(([es, en]) => [en, es])
);

// Provider component
export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  
  // Determine language from URL path
  const isEnglish = location.pathname.startsWith('/en');
  const lang = isEnglish ? 'en' : 'es';
  const t = translations[lang];
  
  // Helper to get correct path for current language
  const getPath = (basePath) => {
    if (isEnglish) {
      return basePath === '/' ? '/en' : `/en${basePath}`;
    }
    return basePath;
  };
  
  // Helper to get the equivalent path in the other language (slugs differ per language)
  const getSwitchPath = (targetLang) => {
    const currentPath = location.pathname;

    if (targetLang === 'en') {
      if (isEnglish) return currentPath; // Already English
      return ES_TO_EN[currentPath] || '/en';
    } else {
      if (!isEnglish) return currentPath; // Already Spanish
      return EN_TO_ES[currentPath] || '/';
    }
  };
  
  const value = {
    lang,
    t,
    isEnglish,
    getPath,
    getSwitchPath,
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
